import { Typography } from "@mui/material";
import React from "react";
import ReactQuill from "react-quill";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import { Col, Row } from "reactstrap";
import DisTable from "src/components/TableV2/Table";
import { Tabs } from "src/components/NavTabs/Tabs";
import { Container as DetailContainer } from "src/components/Container/Container";
import PromptModal from "src/components/ConfirmationModal/PromptModalV2";
import StatusBadge from "src/components/StatusBadgeV2/StatusBadgeV2";
import Toast from "src/components/Toast/Toast";
import useVariableFormatting from "src/hooks/useVariableFormatting";
import { IIOCampaignDetailResponse } from "src/store/models/campaign.model";
import { IIOTemplateStep, MessageQueueMessage } from "src/store/models/template.model";
import * as S from "../../constants/StringConstants";
import { IRootState } from "../../store/reducers";
import { fetchCampaign } from "../../store/reducers/campaign";
import { deleteCampaign } from "../CampaignBuilder/api";
import { addNewlines } from "../CampaignBuilder/utils";
import "./CampaignDetail.scss";
import { StepCharts } from "./StepCharts";
import {
  formatCADateTimeWithComma,
  formatCADateTimeWithCommaLowercaseTime,
  formatDate,
  formatTime,
  stopSendingCampaignNotifications
} from "./utils";

export const MessageStatuses = {
  1: S.CAMP_PERFORMANCE_TO_SEND,
  2: S.CAMP_PERFORMANCE_SENT,
  4: S.CAMP_PERFORMANCE_ERROR,
  6: S.CAMP_PERFORMANCE_PAUSED,
};

enum QueueMessageStatus {
  ToSend = 1,
  Sent = 2,
  Error = 4,
  Paused = 6,
}

enum CampaignTypes {
  IO = 1,
  Proposal = 2,
}

interface ICampaignProps
  extends StateProps,
    DispatchProps,
    RouteComponentProps<{
      id: string;
    }> {}

interface State {
  deleteCampaignPrompt: boolean;
  loading: boolean;
}

interface PreviewProps {
  stepInformation: MessageQueueMessage;
}

const PreviewEmail = (props: PreviewProps) => {
  const { stepInformation } = props;

  const editorRef = useVariableFormatting(null, stepInformation.content);

  return (
    <>     
      <div className="message-details">
        <Row>
          <Col>
            <strong>{S.TPL_MAIL_SUBJECT}:</strong> {stepInformation.subject || "-"}
          </Col>
        </Row>
        <Row>
          <Col>
            <strong>{S.TPL_MAIL_DATE}:</strong> {stepInformation.sentDateTime || "-"}
          </Col>
        </Row>
        <Row>
          <Col>
            <strong>{S.TPL_MAIL_TO}:</strong> {stepInformation.to || "-"}
          </Col>
        </Row>
        <Row>
          <Col>
            <strong>{S.TPL_MAIL_FROM}:</strong> {stepInformation.from || "-"}
          </Col>
        </Row>
        <Row>
          <Col>
            <strong>{S.TPL_MAIL_CC}:</strong> {stepInformation.cc || "-"}
          </Col>
        </Row>
        <Row>
          <Col>
            <strong>{S.TPL_MAIL_BCC}:</strong> {stepInformation.bcc || "-"}
          </Col>
        </Row>
        <Row>
          <Col>
            <strong>{S.TPL_MAIL_REPLY_TO}:</strong> {stepInformation.replyTo || "-"}
          </Col>
        </Row>
      </div>
      <div className="message-body">
        <Row>
          <Col>
            <ReactQuill ref={editorRef} theme="bubble" defaultValue={stepInformation.content} readOnly />
          </Col>
        </Row>
      </div>
    </>
  );
};

const PreviewSms = (props: PreviewProps) => {
  const { stepInformation } = props;

  const editorRef = useVariableFormatting(null, addNewlines(stepInformation.content));

  return (
    <>     
      <div className="message-details">
        <Row>
          <Col>
            <strong>{S.TPL_MAIL_DATE}:</strong> {stepInformation.sentDateTime || "-"}
          </Col>
        </Row>
        <Row>
          <Col>
            <strong>{S.TPL_MAIL_TO}:</strong> {stepInformation.to || "-"}
          </Col>
        </Row>
        <Row>
          <Col>
            <strong>{S.TPL_MAIL_FROM}:</strong> {stepInformation.from || "-"}
          </Col>
        </Row>
      </div>
      <div className="message-body">
        <Row>
          <Col>
            <ReactQuill ref={editorRef} theme="bubble" defaultValue={stepInformation.content} readOnly />
          </Col>
        </Row>
      </div>
    </>
  );
};

interface StepSummaryProps {
  step: IIOTemplateStep;
}

const StepSummary = ({ step }: StepSummaryProps) => {
  if (!step.messageQueues || step.messageQueues.length === 0) {
    return null;
  }
  const { dueDateTime } = step.messageQueues[0];
  return ( 
    <div className="date-time-list">
      <Row>
        <Col>
          <strong>{S.CAMP_DATE}</strong>: {formatDate(dueDateTime)}
        </Col>
      </Row>
      <Row>
        <Col>
          <strong>{S.CAMP_TIME}</strong>: {formatTime(dueDateTime)}
        </Col>
      </Row>
    </div>
  );
};

enum QueueAction {
  Pause = 1,
  Resume = 2,
}

class CampaignDetails extends React.Component<ICampaignProps, State> {
  constructor(props) {
    super(props);
    this.state = {
      deleteCampaignPrompt: false,
      loading: false,
    };
  }

  componentDidMount(): void {
    this.reload();
  }

  componentDidUpdate(prevProps: ICampaignProps) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.reload();
    }
  }

  getCampaignDetails = () => {
    return this.props.campaign.map[this.getCampaignId()] as unknown as IIOCampaignDetailResponse;
  };

  getCampaignId = () => {
    return this.props.match.params.id;
  };

  reload() {
    const { fetchCampaign, match } = this.props;
    this.setState({ loading: true });
    Promise.resolve(fetchCampaign(match.params.id))
      .catch(() => {})
      .then(() => {
        this.setState({ loading: false });
      });
  }

  pauseForUser = async (item: MessageQueueMessage, action: QueueAction) => {
    const campaignDetails = this.getCampaignDetails();
    await stopSendingCampaignNotifications(campaignDetails.campaignId, item.contactIndex, action);
  };

  editCampaign = async () => {
    this.props.history.push("/campaigns/new?id=" + this.getCampaignId());
  };

  getActiveTabPath = () => {
    const { location } = this.props;
    const base = `/campaigns/${this.getCampaignId()}/`;
    return location.pathname.startsWith(base) ? location.pathname.replace(base, "") : "overview";
  };
  
  render() {
    const campaignDetails = this.getCampaignDetails() as any;
    const activeTab = this.getActiveTabPath();
    
    return (
      <>
        <Toast open={this.state.loading || !campaignDetails} message={S.LOADING} onClose={() => {}} />

        {campaignDetails && campaignDetails.steps && (
          <>
            <div className="nav">
              <h1 className="app-bar__title">
                {[campaignDetails.campaignName, campaignDetails.carrierName].filter(x => !!x).join(" - ")}
              </h1>
              <div>
                <button className="button-v2 primary" onClick={this.editCampaign}>
                  {S.CAMP_EDIT_CAMPAIGN}
                </button>
                <button
                  className="button-v2 primary"
                  onClick={e => {
                    e.stopPropagation();
                    this.setState({ deleteCampaignPrompt: true });
                  }}>
                  {S.CAMP_DELETE_CAMPAIGN}
                </button>
                {this.state.deleteCampaignPrompt && (
                  <PromptModal
                    open={this.state.deleteCampaignPrompt}
                    title={S.CAMP_DELETE_CONFIRM_TITLE}
                    confirmText={S.CAMP_DELETE_CONFIRM_DELETE}
                    rejectText={S.CAMP_DELETE_CONFIRM_CANCEL}
                    onConfirm={async () => {
                      await deleteCampaign(this.getCampaignId());
                      this.setState({ deleteCampaignPrompt: false });
                      this.props.history.push("/campaigns/all");
                    }}
                    onReject={() => this.setState({ deleteCampaignPrompt: false })}>
                    <Typography variant="h6">{S.CAMP_DELETE_MESSAGE}</Typography>
                  </PromptModal>
                )}
              </div>
            </div>

            <Tabs
              pages={[
                { name: S.CAMP_OVERVIEW, path: "overview" },
                ...campaignDetails.steps.map((step, index, arr) => {
                  const kind = step.kind === "email" ? "Email" : "SMS";
                  const count = arr.filter(s => s.kind === step.kind).length;
                  let label = kind;
                  if (count > 1) {
                    const kindIndex = arr.slice(0, index).filter(s => s.kind === step.kind).length + 1;
                    label = `${kind} ${kindIndex}`;
                  }
                  return { name: label, path: `step-${step.stepId}` };
                }),
              ]}
              pathname={activeTab}
              isNew={false}
              id={this.getCampaignId()}
              setChangeToURL={url => this.props.history.push(url)}
              basePath="campaigns" />

            <DetailContainer>
              {/* overview */}
              {activeTab === "overview" && (
                <div className="campaign-detail__container">
                  <h1 className="campaign-detail__title">{S.CAMP_OVERVIEW}</h1>
                  <div className="summary-list">
                    <Row>
                      <Col>
                        <strong className="summary-list__columns">{S.CAMP_CARRIER_NAME}:</strong> {campaignDetails.carrierType || "-"}
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <strong className="summary-list__columns">{S.CAMP_CREATED_BY}:</strong> {campaignDetails.userName}
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <strong className="summary-list__columns">{S.CAMP_CREATED_DATE}:</strong> {formatCADateTimeWithComma(campaignDetails.updatedDate)}
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <strong className="summary-list__columns">{S.CAMP_CONTACTS}:</strong> {campaignDetails.noOfContacts}
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <strong className="summary-list__columns">{S.CS_JOURNEY}:</strong>
                        <ol>
                          {campaignDetails.steps.map(step => {
                            if (!step.messageQueues) return null;
                            const { dueDateTime } = step.messageQueues[0];
                            return (
                              <li className="summary-list__journey-column" key={step.stepId}>
                                {step.kind === "email" ? "Email" : "SMS"} @ {formatCADateTimeWithComma(dueDateTime)}
                              </li>
                            );
                          })}
                        </ol>
                      </Col>
                    </Row>
                  </div>
                </div>
              )}
            </DetailContainer>

            {/* steps */}
            {campaignDetails.steps.map((step, index) => {
              const stepPath = `step-${step.stepId}`;
              if (activeTab !== stepPath) return null;
              return (
                <div key={step.stepId}>
                  <DetailContainer>
                    <div className="campaign-detail__container">
                      <h1 className="campaign-detail__performance-title">{S.CAMP_PERFORMANCE}</h1>
                      <StepSummary step={step} />
                      <StepCharts messageQueues={step.messageQueues} />
                    </div>
                  </DetailContainer>
                  <DetailContainer>
                    <div className="campaign-detail__container">
                      <h4 className="campaign-detail__subtitle">
                        Step {index + 1}: {step.kind === "email" ? "Email" : "SMS"}
                      </h4>
                    </div>
                    <MessageList
                      key={step.messageQueues?.map(m => m.status).join(",")}
                      step={step}
                      isProposal={campaignDetails.campaignTypeId === CampaignTypes.Proposal}
                      rowData={JSON.parse(campaignDetails.rowData)}
                      pauseForUser={async item => {
                        await this.pauseForUser(item, QueueAction.Pause);
                        this.reload();
                      }}
                      resumeForUser={async item => {
                        await this.pauseForUser(item, QueueAction.Resume);
                        this.reload();
                      }} />
                  </DetailContainer>
                  <DetailContainer>
                    <div className="campaign-detail__container">
                      <h4 className="campaign-detail__subtitle">
                        {step.kind === "email" ? "Email Body" : "SMS Body"}
                      </h4>
                    </div>
                    {step.kind === "email" ? (
                      <PreviewEmail stepInformation={step} />
                    ) : (
                      <PreviewSms stepInformation={step} />
                    )}
                  </DetailContainer>
                </div>
              );
            })}
          </>
        )}
      </>
    );
  }
}

interface MessageListProps {
  step: IIOTemplateStep;
  rowData: any;
  isProposal: boolean;
  pauseForUser: (message: MessageQueueMessage) => void;
  resumeForUser: (message: MessageQueueMessage) => void;
}

const MessageList = ({ step, isProposal, rowData, pauseForUser, resumeForUser }: MessageListProps) => {
  const renderEmail = (list: string) => {
    const emails = list
      .split(",")
      .map(x => x.trim())
      .filter(x => !!x);
    if (emails.length >= 1) {
      return <span title={emails.join("\n")}>{emails[0]}</span>;
    } else {
      return null;
    }
  };

  const columns = [
    ...(isProposal
      ? [
          {
            key: "policyNumber",
            title: S.CAMP_COL_PROPOSAL,
            style: { minWidth: "6rem", width: "12rem" },
            sortKey: "status",
            render: item => {
              const id = rowData[item.contactIndex - 1].policyNumber;
              return (
                <Link className="proposal-id-link" target="_blank" to={`/proposals/${id}`}>
                  {id}
                </Link>
              );
            },
          },
        ]
      : []),
    {
      key: "to",
      title: S.CAMP_COL_TO,
      style: { minWidth: "6rem", width: "12rem" },
      sortKey: "to",
      render: item => {
        return renderEmail(item.to);
      },
    },
    ...(step.kind === "email"
      ? [
          {
            key: "cc",
            title: S.CAMP_COL_CC,
            style: { minWidth: "6rem", width: "12rem" },
            sortKey: "cc",
            render: item => {
              return renderEmail(item.cc);
            },
          },
        ]
      : []),
    {
      key: "dueDateTime",
      title: S.CAMP_COL_DUE_DATE,
      style: { minWidth: "6rem", width: "12rem" },
      sortKey: "dueDateTime",
      render: item => {
        return formatCADateTimeWithCommaLowercaseTime(item.dueDateTime);
      },
    },
    {
      key: "sentDateTime",
      title: S.CAMP_COL_SENT_DATE,
      style: { minWidth: "6rem", width: "12rem" },
      sortKey: "sentDateTime",
      render: item => {
        return formatCADateTimeWithCommaLowercaseTime(item.sentDateTime);
      },
    },
    {
      key: "status",
      title: S.CAMP_COL_STATUS,
      style: { minWidth: "6rem", width: "12rem" },
      sortKey: "status",
      render: item => {
        let statusKey = "";
        let statusLabel = MessageStatuses[item.status];

        switch (item.status) {
          case QueueMessageStatus.ToSend:
            statusKey = "campaigns to-send";
            break;
          case QueueMessageStatus.Sent:
            statusKey = "campaigns sent";
            break;
          case QueueMessageStatus.Error:
            statusKey = "campaigns error";
            break;
          case QueueMessageStatus.Paused:
            statusKey = "campaigns paused";
            break;
          default:
            statusKey = statusLabel.toLowerCase();
        }

        return <StatusBadge status={statusKey} label={statusLabel} />;
      },
    },
    {
      key: "error",
      title: S.CAMP_COL_ERRORS,
      style: { minWidth: "6rem", width: "12rem" },
      sortKey: "error",
    },
    {
      key: "delete",
      title: S.CAMP_COL_ACTIONS,
      style: { width: "7rem" },
      render: item => {
        if (item.status == QueueMessageStatus.ToSend) {
          return (
            <button className="button-v2 button-v2__secondary-mini" onClick={() => pauseForUser(item)}>
              {S.CAMP_PAUSE}
            </button>
          );
        }
        if (item.status == QueueMessageStatus.Paused) {
          return (
            <button className="button-v2 button-v2__secondary-mini" onClick={() => resumeForUser(item)}>
              {S.CAMP_RESUME}
            </button>
          );
        }
        return <></>;
      },
    },
  ];

  const total = step.messageQueues.length;
  const listOptions = {};
  const map = null;
  const projection = null;
  return (
    <div className="steps-table">
      <DisTable<MessageQueueMessage>
        columns={columns}
        initialState={{ rows: 25, map, projection, total, ...listOptions }}
        onUpdate={async ({ page, rows, sort, query }) => {
          return {
            total,
            data: step.messageQueues.slice((page - 1) * rows, page * rows),
          };
        }}></DisTable>
    </div>
  );
};

const mapStateToProps = ({ campaign }: IRootState) => ({ campaign });
const mapDispatchToProps = { fetchCampaign };

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(CampaignDetails);
